import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { connectionFor, detailOf, pathFor, stepStatus } from './steps'

type Recovery = Schema<'RecoveryResponse'>

const PROVIDER = 'google_search_console'

/**
 * Step 2 — S-D2's `s2` and `s2-fail`.
 *
 * The scope is named **before** the button, not after: people decline OAuth when
 * they cannot tell what is being asked for, and "read-only" is the whole
 * reassurance here. The bullets name **one permission per scope in
 * `oauth.SCOPES`**, so widening that tuple later means changing this copy in the
 * same commit.
 *
 * **`siteverification` was missing from the list until `GOOG3` (2026-08-08)**, and
 * that is the second time this screen has been out of step with the consent screen
 * behind its own button. The first was *"we never read: Analytics"* while
 * `analytics.readonly` sat registered with no code behind it (`GOOG1`); this one
 * was quieter and had the same shape — the list said *"anything outside those
 * two"* while Google's consent screen renders **three** tick boxes. Shot 3 of
 * `docs/GOOGLE-OAUTH-DEMO-SHOOT.md` is this frame and Shot 4 is that consent
 * screen, so a reviewer meets both within forty seconds: §8's rejection cause 1
 * reached by way of the app's own copy rather than by a missing demonstration.
 *
 * `no_access` is its own screen, not a variant of the failure card, because the
 * recovery is genuinely different: the property exists and this Google account
 * cannot see it, which is usually somebody's agency owning it. All three routes
 * S-D2 drew are here — try a different account (press Connect again, which sends
 * a fresh consent), have us draft the email, or verify by DNS.
 */
export function SearchConsoleStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const connection = connectionFor(state, PROVIDER)
  const step = stepStatus(state, 'search_console')
  const [recovery, setRecovery] = useState<Recovery | null>(null)

  const connect = useMutation(() =>
    call(() => api.POST('/api/v1/onboarding/search-console/connect', {})),
  )
  const loadRecovery = useMutation(() =>
    call(() => api.GET('/api/v1/onboarding/search-console/recovery', {})),
  )

  async function onConnect(): Promise<void> {
    setRecovery(null)
    const result = await connect.run(undefined)
    if (!result.ok) return
    adopt(result.data.state)
    if (result.data.redirect_url) {
      // A full navigation: this is Google's consent screen, and `fetch` would
      // hand it back as an opaque body nobody can render.
      window.location.assign(result.data.redirect_url)
      return
    }
    if (result.data.status === 'connected') void navigate(pathFor(result.data.state.current_step))
  }

  async function onRecovery(): Promise<void> {
    const result = await loadRecovery.run(undefined)
    if (result.ok) setRecovery(result.data)
  }

  const noAccess = connection?.status === 'no_access'
  const listed = detailOf(connection).properties
  const properties = Array.isArray(listed) ? (listed as string[]) : []

  return (
    <div className="app-wiz">
      <WizardChrome current="search_console" />

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>
            {noAccess
              ? `${state.site?.domain ?? 'Your site'} isn't in this Google account`
              : 'Connect Google Search Console'}
          </h3>
        </div>
        <div className="ar-card__body ar-stack">
          <InlineError failure={connect.state.failure ?? loadRecovery.state.failure} />

          {noAccess ? (
            <>
              <div className="ar-alert ar-alert--warning" role="alert">
                <span className="ar-alert__icon" aria-hidden="true">
                  !
                </span>
                <div className="ar-alert__body">
                  <strong>We connected, but found no matching property</strong>
                  {connection.account_label ? (
                    <>
                      You signed in as <strong>{connection.account_label}</strong>
                      {properties.length > 0
                        ? `, which has ${String(properties.length)} ${
                            properties.length === 1 ? 'property' : 'properties'
                          } — none of them is this site.`
                        : ', which owns no matching property.'}
                    </>
                  ) : (
                    'That Google account owns no property matching this site.'
                  )}
                </div>
              </div>

              {properties.length > 0 ? (
                <ul className="ar-muted" style={{ margin: 0 }}>
                  {properties.map((property) => (
                    <li key={property}>{property}</li>
                  ))}
                </ul>
              ) : null}

              <div className="ar-stack ar-stack--tight">
                <strong>Three ways forward</strong>

                <div className="app-conn">
                  <span className="app-conn__logo" aria-hidden="true">
                    1
                  </span>
                  <span className="app-conn__body">
                    <strong>Sign in with a different Google account</strong>
                    <span>If you have another account that owns the property.</span>
                  </span>
                  <Button
                    size="sm"
                    busy={connect.pending}
                    busyLabel="Opening Google"
                    onClick={() => {
                      void onConnect()
                    }}
                  >
                    Switch account
                  </Button>
                </div>

                <div className="app-conn">
                  <span className="app-conn__logo" aria-hidden="true">
                    2
                  </span>
                  <span className="app-conn__body">
                    <strong>Ask whoever manages your site</strong>
                    <span>We&rsquo;ll draft the email — they just add you in Search Console.</span>
                  </span>
                  <Button
                    size="sm"
                    busy={loadRecovery.pending}
                    busyLabel="Drafting"
                    onClick={() => {
                      void onRecovery()
                    }}
                  >
                    Draft email
                  </Button>
                </div>

                <div className="app-conn">
                  <span className="app-conn__logo" aria-hidden="true">
                    3
                  </span>
                  <span className="app-conn__body">
                    <strong>Verify the site yourself</strong>
                    <span>Add a DNS TXT record. We&rsquo;ll show the exact value to paste.</span>
                  </span>
                  <Button
                    size="sm"
                    busy={loadRecovery.pending}
                    busyLabel="Fetching"
                    onClick={() => {
                      void onRecovery()
                    }}
                  >
                    Show record
                  </Button>
                </div>
              </div>

              {recovery ? <RecoveryDetail recovery={recovery} /> : null}
            </>
          ) : (
            <>
              <p className="ar-muted" style={{ margin: 0 }}>
                This is where your real ranking and click data comes from. Without it we can still
                audit and write, but the dashboard will be mostly empty.
              </p>

              <div className="app-conn">
                <span className="app-conn__logo" aria-hidden="true">
                  G
                </span>
                <span className="app-conn__body">
                  <strong>Google Search Console</strong>
                  <span>
                    Read-only. We never post, change, or delete anything in your Google account.
                  </span>
                </span>
                <Button
                  tone="primary"
                  busy={connect.pending}
                  busyLabel="Opening Google"
                  onClick={() => {
                    void onConnect()
                  }}
                >
                  {connection?.status === 'connected' ? 'Reconnect' : 'Connect'}
                </Button>
              </div>

              <ul
                className="ar-muted"
                style={{
                  fontSize: 'var(--ar-text-body-sm-size)',
                  margin: 0,
                  paddingLeft: 'var(--ar-space-5)',
                }}
              >
                <li>
                  We read from Search Console: impressions, clicks, average position, and your
                  verified property list.
                </li>
                <li>
                  We read from Google Analytics: sessions, engaged sessions, users, and key events —
                  so you can see whether a page that rose in search actually gained visits.
                  Read-only; we never change anything in Analytics.
                </li>
                <li>
                  We ask Google for the site-ownership record: if your site isn&rsquo;t in Search
                  Console yet, we fetch the DNS entry that proves it&rsquo;s yours and show it to
                  you. You add it and Google does the verifying — we only display it.
                </li>
                <li>We never read: Ads, Gmail, Drive, or anything outside the three above.</li>
                <li>
                  You can grant each permission on its own. Decline Analytics and everything else
                  still works, without the traffic figures.
                </li>
                <li>You can disconnect at any time from Settings → Connections.</li>
              </ul>

              {connection?.status === 'connected' ? (
                <div className="ar-alert ar-alert--success" role="status">
                  <span className="ar-alert__icon" aria-hidden="true">
                    ✓
                  </span>
                  <div className="ar-alert__body">
                    <strong>Connected</strong>
                    {connection.account_label ? `Signed in as ${connection.account_label}.` : null}
                  </div>
                </div>
              ) : step?.status === 'failed' && detailOf(step).message ? (
                <div className="ar-alert ar-alert--danger" role="alert">
                  <span className="ar-alert__icon" aria-hidden="true">
                    !
                  </span>
                  <div className="ar-alert__body">
                    <strong>We could not connect Search Console</strong>
                    {String(detailOf(step).message)}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <WizardFooter current="search_console">
          <Button
            tone="primary"
            onClick={() => {
              const next = state.steps.find((row) => row.step === 'linkedin')
              void navigate(pathFor(next?.step ?? 'linkedin'))
            }}
          >
            Continue
          </Button>
        </WizardFooter>
      </section>
    </div>
  )
}

/**
 * Routes 2 and 3, once fetched.
 *
 * The DNS record is absent when Google cannot mint the token — the wizard says
 * so rather than printing a record that would never verify, which is the whole
 * reason `verification_record` is nullable.
 */
function RecoveryDetail({ recovery }: { recovery: Recovery }): JSX.Element {
  return (
    <div className="ar-panel ar-stack ar-stack--tight">
      <strong>The email to send</strong>
      <p className="ar-muted" style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)' }}>
        Subject: {recovery.owner_request_subject}
      </p>
      <pre
        className="ar-mono"
        style={{
          margin: 0,
          whiteSpace: 'pre-wrap',
          fontSize: 'var(--ar-text-caption-size)',
        }}
      >
        {recovery.owner_request_body}
      </pre>

      <strong>The DNS record</strong>
      {recovery.verification_record ? (
        <p className="ar-mono" style={{ margin: 0, fontSize: 'var(--ar-text-caption-size)' }}>
          {recovery.verification_record.kind} {recovery.verification_record.name} →{' '}
          {recovery.verification_record.value}
        </p>
      ) : (
        <p className="ar-muted" style={{ margin: 0, fontSize: 'var(--ar-text-body-sm-size)' }}>
          Google issues this value, so we can only show it once Search Console is connected. Use one
          of the other two routes for now.
        </p>
      )}
    </div>
  )
}
